import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

interface Project {
  id: string;
  title: string;
  status: string;
  client_name: string;
  freelancer_name?: string;
  last_message?: {
    content: string;
    sender: string;
    time: string;
  };
}

const Messages: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // Load client projects if client, freelancer projects if freelancer
      const endpoint = user?.role === 'client' 
        ? '/api/projects/client'
        : '/api/projects/freelancer';
      
      const response = await axios.get(
        `http://localhost:5000${endpoint}?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Filter projects that have been assigned (have a freelancer for clients, or are assigned for freelancers)
        const filteredProjects = response.data.data.projects.filter((project: Project) => {
          if (user?.role === 'client') {
            return project.freelancer_name; // Only projects with assigned freelancers
          } else {
            return project.status === 'active' || project.status === 'in-progress';
          }
        });

        // For each project, load the last message
        const projectsWithMessages = await Promise.all(
          filteredProjects.map(async (project: Project) => {
            try {
              const messagesResponse = await axios.get(
                `http://localhost:5000/api/projects/${project.id}/messages`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                }
              );

              if (messagesResponse.data.success && messagesResponse.data.data.messages.length > 0) {
                const lastMessage = messagesResponse.data.data.messages[messagesResponse.data.data.messages.length - 1];
                return {
                  ...project,
                  last_message: {
                    content: lastMessage.message,
                    sender: lastMessage.sender_name,
                    time: lastMessage.created_at
                  }
                };
              }
            } catch (error) {
              console.error('Error loading messages for project:', project.id, error);
            }
            return project;
          })
        );

        setProjects(projectsWithMessages);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]); // Added user as dependency

  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user, loadProjects]); // Added loadProjects as dependency

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 7) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMins > 0) {
      return `${diffMins}m ago`;
    } else {
      return 'Just now';
    }
  };

  const getOtherPartyName = (project: Project) => {
    if (user?.role === 'client') {
      return project.freelancer_name || 'Freelancer';
    } else {
      return project.client_name;
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getOtherPartyName(project).toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeFilter === 'active') {
      return matchesSearch && (project.status === 'active' || project.status === 'in-progress');
    } else if (activeFilter === 'completed') {
      return matchesSearch && project.status === 'completed';
    }
    
    return matchesSearch;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.username?.toUpperCase()}</span>
                <div className="user-meta">
                  <span className={`role-badge role-${user?.role}`}>{user?.role}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages</h1>
              <p className="text-gray-600">
                Communicate with {user?.role === 'client' ? 'freelancers' : 'clients'} about your projects
              </p>
            </div>
            <button 
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary"
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search projects or people..."
                    className="form-input pl-10 w-full"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-4 py-2 rounded-lg ${
                    activeFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveFilter('active')}
                  className={`px-4 py-2 rounded-lg ${
                    activeFilter === 'active'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setActiveFilter('completed')}
                  className={`px-4 py-2 rounded-lg ${
                    activeFilter === 'completed'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border'
                  }`}
                >
                  Completed
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Projects List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredProjects.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No messages yet</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? 'No projects found matching your search.'
                  : user?.role === 'client'
                  ? 'Start a conversation by assigning a freelancer to your project.'
                  : 'Start a conversation by requesting a project or getting assigned.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => navigate(user?.role === 'client' ? '/post-project' : '/projects')}
                  className="btn btn-primary"
                >
                  {user?.role === 'client' ? 'Post a Project' : 'Browse Projects'}
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/messages/${project.id}`}
                  className="block hover:bg-gray-50 transition-colors duration-150"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            user?.role === 'client' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            <span className={`font-bold ${
                              user?.role === 'client' ? 'text-blue-600' : 'text-green-600'
                            }`}>
                              {getOtherPartyName(project).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold text-gray-900 truncate">
                                {project.title}
                              </h3>
                              {project.last_message && (
                                <span className="text-sm text-gray-500 flex-shrink-0 ml-2">
                                  {formatTime(project.last_message.time)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {user?.role === 'client' ? 'Freelancer: ' : 'Client: '}
                              <span className="font-medium">{getOtherPartyName(project)}</span>
                              <span className="mx-2">•</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                project.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                project.status === 'in-progress' ? 'bg-purple-100 text-purple-800' :
                                project.status === 'completed' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {project.status}
                              </span>
                            </p>
                          </div>
                        </div>

                        {project.last_message ? (
                          <div className="mt-3">
                            <p className="text-sm text-gray-700 line-clamp-2">
                              <span className="font-medium">{project.last_message.sender}: </span>
                              {project.last_message.content}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic mt-3">
                            No messages yet. Start the conversation...
                          </p>
                        )}
                      </div>

                      <div className="ml-4 flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Help Text */}
        {filteredProjects.length > 0 && (
          <div className="mt-6 bg-blue-50 p-4 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Messaging Tips:</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Be clear and specific about project requirements and expectations</li>
                  <li>• Share important files and updates through the project page</li>
                  <li>• Keep communication professional and focused on the project</li>
                  <li>• Use messages to clarify doubts and provide feedback</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;